import { Check, Loader2, Minus, X } from "lucide-react";
import { type KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type TeamsOverview, useSetPersonOverrides } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { cn } from "@/lib/utils";

import {
  appDecision,
  type AppGrant,
  baseAccess,
  EXCEPTION_CHOICES,
  type ExceptionChoice,
  exceptionChoice,
  exceptionSettings,
  exceptionsOf,
  exceptionUnits,
  type GrantRow,
  gridParts,
  namedTeams,
  partAccess,
  type PartState,
  sameExceptions,
  teamLabel,
  teamsOf,
  toolDecision,
  unitsText,
  withExceptionChoice,
} from "./utils";

interface ExceptionsEditorProps {
  /** The person's email, lower case. */
  email: string | null;
  open: boolean;
  /** How the person is shown: their name, else their email. */
  label: string;
  overview: TeamsOverview;
  rows: readonly GrantRow[];
  /** The apps an exception can allow or deny. */
  apps: readonly AppGrant[];
  onOpenChange: (open: boolean) => void;
}

/**
 * One person's exceptions: per group part, per served tool and per app,
 * Volgens team, Toestaan or Weigeren, with the access that results next to it
 * as it is chosen; an app allowed without one of the units it requires says
 * which. Save sends the full list.
 */
export function ExceptionsEditor({
  email,
  open,
  label,
  onOpenChange,
  ...rest
}: ExceptionsEditorProps) {
  const { t } = useTranslation();
  const teams = email ? teamsOf(email, namedTeams(rest.overview)) : [];
  return (
    <Dialog open={open && email !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("teams.people.exceptionsFor", { person: label })}</DialogTitle>
          <DialogDescription>
            {label !== email && <span className="block">{email}</span>}
            <span className="block">
              {t("teams.exceptions.teams", {
                teams: teams.length > 0 ? teams.map(teamLabel).join(", ") : t("teams.defaultTeam"),
              })}
            </span>
            <span className="mt-2 block">{t("teams.exceptions.description")}</span>
          </DialogDescription>
        </DialogHeader>
        {email && (
          <ExceptionsForm
            key={email}
            email={email}
            label={label}
            onClose={() => onOpenChange(false)}
            {...rest}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ExceptionsFormProps extends Omit<
  ExceptionsEditorProps,
  "email" | "open" | "onOpenChange"
> {
  email: string;
  onClose: () => void;
}

function ExceptionsForm({ email, overview, rows, apps, onClose }: ExceptionsFormProps) {
  const { t } = useTranslation();
  const save = useSetPersonOverrides();
  const units = exceptionUnits(rows, apps);
  const [initial] = useState(() => exceptionsOf(email, overview.overrides));
  const [draft, setDraft] = useState<ReadonlyMap<string, boolean>>(initial);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  const base = baseAccess(email, namedTeams(overview), overview.default_team);
  const parts = new Map(partAccess(rows, base, draft).map((access) => [access.part.unit, access]));
  const choose = (unit: string, choice: ExceptionChoice) =>
    setDraft((current) => withExceptionChoice(current, unit, choice));

  const submit = async () => {
    setFailure(null);
    try {
      await save.mutateAsync({ email, overrides: exceptionSettings(draft, units) });
      onClose();
    } catch (err) {
      setFailure(inlineFailure({ error: err }));
    }
  };
  const error = failure && apiErrorMessage(failure.error, "saveExceptions");

  return (
    <>
      <div className="@container overflow-hidden rounded-lg border text-sm">
        {rows.map((row) => (
          <div
            key={row.id}
            role="group"
            aria-label={row.title}
            className="border-t first:border-t-0"
          >
            <div className="bg-accent-light text-foreground px-3 py-1.5 font-semibold">
              {row.title}
            </div>
            {gridParts([row]).map((part) => (
              <div key={part.unit}>
                <ExceptionRow
                  label={t(`teams.exceptions.allTools.${part.access}`)}
                  note={part.comingSoon ? t("tools.comingSoon") : null}
                  unitLabel={t("teams.partTitle", {
                    group: row.title,
                    access: t(`tools.access.${part.access}`),
                  })}
                  choice={exceptionChoice(draft, part.unit)}
                  onChoose={(choice) => choose(part.unit, choice)}
                  result={parts.get(part.unit)?.state ?? "none"}
                  strong
                  disabled={save.isPending}
                />
                {part.tools.map((tool) => (
                  <ExceptionRow
                    key={tool.name}
                    label={tool.title}
                    note={tool.paused ? t("tools.paused") : null}
                    unitLabel={tool.title}
                    choice={exceptionChoice(draft, tool.name)}
                    onChoose={(choice) => choose(tool.name, choice)}
                    result={toolDecision(tool, base, draft).allowed ? "all" : "none"}
                    disabled={save.isPending}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
        {apps.length > 0 && (
          <div
            role="group"
            aria-label={t("teams.apps.title")}
            className="border-t first:border-t-0"
          >
            <div className="bg-accent-light text-foreground px-3 py-1.5 font-semibold">
              {t("teams.apps.title")}
            </div>
            {apps.map((app) => {
              const decision = appDecision(app, base, draft, rows);
              return (
                <ExceptionRow
                  key={app.unit}
                  label={app.title}
                  note={t("teams.apps.requires", { units: unitsText(app.requires, rows) })}
                  unitLabel={app.title}
                  choice={exceptionChoice(draft, app.unit)}
                  onChoose={(choice) => choose(app.unit, choice)}
                  result={decision.allowed ? "all" : "none"}
                  missing={
                    decision.unit.allowed && decision.missing.length > 0
                      ? unitsText(decision.missing, rows)
                      : null
                  }
                  strong
                  disabled={save.isPending}
                />
              );
            })}
          </div>
        )}
      </div>
      {error && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
      <DialogFooter className="gap-2 sm:justify-between">
        <Button
          variant="ghost"
          onClick={() => setDraft(new Map())}
          disabled={save.isPending || draft.size === 0}
        >
          {t("teams.exceptions.clearAll")}
        </Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={save.isPending || sameExceptions(draft, initial)}
          >
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

interface ExceptionRowProps {
  label: string;
  /** A muted line under the label: coming soon, or switched off for everyone. */
  note: string | null;
  /** What the choice is about, for screen readers: "Klanten: Lezen". */
  unitLabel: string;
  choice: ExceptionChoice;
  onChoose: (choice: ExceptionChoice) => void;
  result: PartState;
  /** For an app allowed without all it requires: the units it lacks, in words. */
  missing?: string | null;
  /** A group part's row, in bold; a tool's row is indented under it. */
  strong?: boolean;
  disabled: boolean;
}

/**
 * One part or tool: its name, the three-way choice and the resulting access.
 * When the list has less than 580 px (a phone, or a window just over 640 px,
 * where the dialog keeps a margin), the choice moves under the name, so
 * nothing scrolls sideways and a long Dutch name such as "Omzetoverzicht"
 * keeps its own line. The list's width decides (a container query).
 */
function ExceptionRow({
  label,
  note,
  unitLabel,
  choice,
  onChoose,
  result,
  missing = null,
  strong = false,
  disabled,
}: ExceptionRowProps) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-t px-3 py-1.5 @min-[580px]:grid-cols-[minmax(0,1fr)_auto_7rem]">
      <div className={cn("min-w-0", !strong && "pl-4")}>
        <span className={cn("text-foreground block", strong && "font-medium")}>{label}</span>
        {note && <span className="text-muted block text-xs">{note}</span>}
      </div>
      <div className="order-last col-span-2 @min-[580px]:order-none @min-[580px]:col-span-1">
        <ChoiceControl
          value={choice}
          onChange={onChoose}
          label={t("teams.exceptions.choiceLabel", { unit: unitLabel })}
          disabled={disabled}
        />
      </div>
      <Result state={result} missing={missing} />
    </div>
  );
}

/** The chosen option's colours: following the team stays quiet, allow is blue, deny red. */
const CHOSEN: Record<ExceptionChoice, string> = {
  team: "bg-card text-foreground shadow-xs",
  allow: "bg-primary text-primary-foreground",
  deny: "bg-destructive text-destructive-foreground",
};

interface ChoiceControlProps {
  value: ExceptionChoice;
  onChange: (choice: ExceptionChoice) => void;
  label: string;
  disabled: boolean;
}

/**
 * Volgens team, Toestaan or Weigeren as one segmented control, a radio group:
 * Tab reaches the chosen option, the arrows move the choice.
 */
function ChoiceControl({ value, onChange, label, disabled }: ChoiceControlProps) {
  const { t } = useTranslation();
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) {
      return;
    }
    event.preventDefault();
    const count = EXCEPTION_CHOICES.length;
    const next = EXCEPTION_CHOICES[(EXCEPTION_CHOICES.indexOf(value) + step + count) % count];
    if (next) {
      onChange(next);
      event.currentTarget.querySelector<HTMLButtonElement>(`[data-choice="${next}"]`)?.focus();
    }
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="bg-muted/10 inline-flex rounded-md border p-0.5"
    >
      {EXCEPTION_CHOICES.map((choice) => (
        <button
          key={choice}
          type="button"
          role="radio"
          data-choice={choice}
          aria-checked={value === choice}
          tabIndex={value === choice ? 0 : -1}
          onClick={() => onChange(choice)}
          disabled={disabled}
          className={cn(
            "focus-visible:ring-ring rounded-sm px-2 py-1 text-xs font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50 pointer-coarse:py-3",
            value === choice ? CHOSEN[choice] : "text-muted hover:text-foreground",
          )}
        >
          {t(`teams.exceptions.choice.${choice}`)}
        </button>
      ))}
    </div>
  );
}

interface ResultProps {
  state: PartState;
  /** No access because these required units are missing, in words. */
  missing?: string | null;
}

/**
 * The access that results: all of it, part of it (a group part), or none;
 * for an app that lacks a required unit, which one.
 */
function Result({ state, missing = null }: ResultProps) {
  const { t } = useTranslation();
  if (state !== "all" && missing) {
    return (
      <span className="text-muted inline-flex items-start justify-end gap-1 text-right text-xs">
        <X aria-hidden="true" className="mt-px size-3.5 shrink-0" />
        {t("teams.apps.exceptionMissing", { units: missing })}
      </span>
    );
  }
  if (state === "all") {
    return (
      <span className="text-primary inline-flex items-center justify-end gap-1 text-xs font-medium whitespace-nowrap">
        <Check aria-hidden="true" className="size-3.5" />
        {t("teams.exceptions.allowed")}
      </span>
    );
  }
  if (state === "some") {
    return (
      <span className="text-foreground inline-flex items-center justify-end gap-1 text-xs font-medium whitespace-nowrap">
        <Minus aria-hidden="true" className="size-3.5" />
        {t("teams.exceptions.partly")}
      </span>
    );
  }
  return (
    <span className="text-muted inline-flex items-center justify-end gap-1 text-xs whitespace-nowrap">
      <X aria-hidden="true" className="size-3.5" />
      {t("teams.exceptions.denied")}
    </span>
  );
}
